import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiTarget, FiGlobe, FiHeart, FiBookOpen, FiAward } = FiIcons;

const AboutUs = () => {
  const teamMembers = [
    {
      name: 'John Koroma',
      role: 'Founder & CEO',
      avatar: 'https://images.unsplash.com/photo-1531384441138-2736e62e0919?w=150&h=150&fit=crop&crop=face',
      bio: 'Passionate about education and youth development in Sierra Leone.'
    },
    {
      name: 'Aminata Sesay',
      role: 'Education Director',
      avatar: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=150&h=150&fit=crop&crop=face',
      bio: 'Former teacher with 15 years of experience in curriculum development.'
    },
    {
      name: 'Ibrahim Jalloh',
      role: 'Community Manager',
      avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=face',
      bio: 'Dedicated to building meaningful connections between students and mentors.'
    },
    {
      name: 'Fatmata Kamara',
      role: 'Program Coordinator',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face',
      bio: 'Coordinates our leadership programs across Sierra Leone and The Gambia.'
    }
  ];

  const achievements = [
    { value: '5,000+', label: 'Students Impacted' },
    { value: '45+', label: 'Schools Partnered' },
    { value: '12', label: 'Countries Reached' },
    { value: '95%', label: 'Student Satisfaction' }
  ];

  return (
    <div className="space-y-8 pb-16 md:pb-0">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
      >
        <div className="text-center">
          <SafeIcon icon={FiUsers} className="w-16 h-16 mx-auto mb-4 text-primary-600" />
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">About ILM Global</h1>
          <p className="text-gray-600 max-w-3xl mx-auto text-sm md:text-base">
            Inspirational Leadership Movement (ILM) Global is a non-profit organization dedicated to 
            empowering young people through education, mentorship, and leadership development in 
            Sierra Leone and The Gambia. Founded in 2015, we've been on a mission to transform lives 
            and communities through accessible quality education.
          </p>
        </div>
      </motion.div>

      {/* Our Story */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
      >
        <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
              <SafeIcon icon={FiBookOpen} className="w-6 h-6 mr-2 text-primary-600" />
              Our Story
            </h2>
            <p className="text-gray-600 mb-4 text-sm md:text-base">
              ILM Global began as a small initiative in Freetown, Sierra Leone, with just 25 students 
              and 3 volunteer mentors. Our founder, John Koroma, witnessed firsthand the challenges 
              young people faced in accessing quality education and mentorship.
            </p>
            <p className="text-gray-600 text-sm md:text-base">
              What started as weekend study sessions in a small classroom has grown into a comprehensive 
              platform reaching thousands of students across West Africa. Our journey has been one of 
              persistence, passion, and the unwavering belief that every young person deserves the 
              opportunity to learn and lead.
            </p>
          </div>
          <div className="flex-1">
            <img 
              src="https://images.unsplash.com/photo-1529390079861-591de354faf5?auto=format&fit=crop&w=800" 
              alt="Students studying together" 
              className="rounded-lg shadow-md w-full h-auto object-cover"
            />
          </div>
        </div>
      </motion.div>

      {/* Key Achievements */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-xl shadow-lg p-6 md:p-8 text-white"
      >
        <h2 className="text-2xl font-bold mb-6 text-center flex items-center justify-center">
          <SafeIcon icon={FiAward} className="w-6 h-6 mr-2" />
          Our Impact
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {achievements.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="bg-white bg-opacity-20 rounded-lg p-4 text-center"
            >
              <div className="text-2xl md:text-3xl font-bold">{item.value}</div>
              <div className="text-sm md:text-base">{item.label}</div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Our Team */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-white rounded-xl shadow-lg p-6 md:p-8 border border-gray-100"
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center flex items-center justify-center">
          <SafeIcon icon={FiHeart} className="w-6 h-6 mr-2 text-primary-600" />
          Meet Our Team
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {teamMembers.map((member, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              className="bg-gray-50 rounded-lg p-4 text-center hover:shadow-md transition-shadow"
            >
              <img
                src={member.avatar}
                alt={member.name}
                className="w-24 h-24 rounded-full mx-auto mb-3 object-cover border-4 border-white shadow-sm"
              />
              <h3 className="text-lg font-semibold text-gray-800">{member.name}</h3>
              <p className="text-primary-600 text-sm mb-2">{member.role}</p>
              <p className="text-gray-600 text-sm">{member.bio}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Contact */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gray-50 rounded-xl shadow-md p-6 md:p-8 border border-gray-100"
      >
        <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">Get In Touch</h2>
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1 space-y-4">
            <p className="text-gray-600 text-sm md:text-base">
              We'd love to hear from you! Whether you're interested in our programs, partnership opportunities, 
              or just want to learn more about our work, please reach out.
            </p>
            <div className="space-y-2">
              <div className="flex items-center">
                <SafeIcon icon={FiGlobe} className="w-5 h-5 text-primary-600 mr-2" />
                <span className="text-gray-700">www.ilmglobal.org</span>
              </div>
              <div className="flex items-center">
                <SafeIcon icon={FiUsers} className="w-5 h-5 text-primary-600 mr-2" />
                <span className="text-gray-700">info@ilmglobal.org</span>
              </div>
              <div className="flex items-start">
                <SafeIcon icon={FiTarget} className="w-5 h-5 text-primary-600 mr-2 mt-1" />
                <span className="text-gray-700">
                  Main Office: 15 Wilkinson Road, Freetown, Sierra Leone<br />
                  Gambia Office: 7 Independence Drive, Banjul, The Gambia
                </span>
              </div>
            </div>
          </div>
          <div className="flex-1">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Your Name</label>
                  <input 
                    type="text" 
                    id="name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                  <input 
                    type="email" 
                    id="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter your email"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    id="message"
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="How can we help you?"
                  ></textarea>
                </div>
                <button className="w-full bg-primary-500 text-white font-medium py-2 px-4 rounded-md hover:bg-primary-600 transition-colors">
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AboutUs;